import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAuth } from "hooks/useAuth";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useGetDDLSmsProviderListQuery } from "store/services/smsProviderApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";

const INITIAL_STATE = {
  account: "",
  entity: "",
  provider: "",
  parameters: {},
  isDefault: false,
  isActive: true,
  vendor: "",
};

const SmsIntegrationForm = ({
  isBusy = false,
  smsIntegrationDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // RTK Query
  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const { data: providers, isLoading: loadingProviders } = useGetDDLSmsProviderListQuery();
  const [fetchVendors, { data: vendors, isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileQuery();

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
    },
  });
  const watchedAccount = useWatch({ control, name: "account" });
  const watchedProvider = useWatch({ control, name: "provider" });

  // Set default parameters based on provider
  React.useEffect(() => {
    if (smsIntegrationDetail) return;
    if (watchedProvider?.name) {
      let defaultParameters = {};
      if (watchedProvider.name.toLowerCase() === "aakash sms") {
        defaultParameters = {
          from: "<your-approved-sender-id>",
          auth_token: "<aakash-api-token>",
        };
      } else if (watchedProvider.name.toLowerCase() === "sparrow sms") {
        defaultParameters = {
          from: "<your-approved-sender-id>",
          token: "<-sms-api-token>",
        };
      }
      setValue("parameters", JSON.stringify(defaultParameters, null, 2));
    }
  }, [watchedProvider, setValue, smsIntegrationDetail]);

  // Handle form submission
  const handleOnSmsIntegrationSubmit = (data) => {
    const _smsIntegrationPayload = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      provider: data?.provider?._id,
      vendor: data?.vendor?._id,
      parameters: data?.parameters ? JSON.parse(data.parameters) : {},
    };
    onSubmit(_smsIntegrationPayload);
  };

  // Populate form for editing
  React.useEffect(() => {
    if (!smsIntegrationDetail) return;

    reset({
      ...INITIAL_STATE,
      account: smsIntegrationDetail?.account ?? viewEntity?.entity?.account ?? "",
      entity: smsIntegrationDetail?.entity ?? viewEntity?.entity ?? "",
      provider: smsIntegrationDetail?.provider ?? "",
      parameters: smsIntegrationDetail?.parameters
        ? JSON.stringify(smsIntegrationDetail.parameters, null, 2)
        : "{}",
      isDefault: smsIntegrationDetail?.isDefault ?? false,
      isActive: smsIntegrationDetail?.isActive ?? true,
      vendor: smsIntegrationDetail?.vendor ?? "",
    });
  }, [smsIntegrationDetail, reset, viewEntity?.entity]);

  // Fetch entities and vendors
  React.useEffect(() => {
    fetchVendors();
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: watchedAccount?._id || viewEntity?.entity?.account,
      });
    }
  }, [
    isAccountAdmin,
    isSystemAdmin,
    watchedAccount?._id,
    fetchEntities,
    viewEntity?.entity?.account,
    fetchVendors,
  ]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={2}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Account"
                    required
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("entity", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Controller
              name="provider"
              control={control}
              rules={{ required: "Please select a provider." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Provider"
                  value={value ?? ""}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.name ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={providers || []}
                  loading={loadingProviders}
                  loadingText="Loading Providers..."
                  helperText={errors?.provider?.message}
                  error={!!errors?.provider}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="vendor"
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Vendor (Optional)"
                  value={value ?? ""}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option ? `${option?.ownerName} - ${option?.shopName}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={vendors || []}
                  loading={loadingVendors}
                  loadingText="Loading Vendors..."
                  helperText={errors?.vendor?.message}
                  error={!!errors?.vendor}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="parameters"
              control={control}
              rules={{
                required: "Parameters are required.",
                validate: (value) => {
                  try {
                    JSON.parse(value);
                    return true;
                  } catch {
                    return "Invalid JSON format";
                  }
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Parameters (JSON)"
                  multiline
                  rows={4}
                  error={!!errors.parameters}
                  helperText={
                    errors?.parameters?.message ||
                    "Enter parameters in JSON format (e.g., {\"from\": \"TMS\", \"token\": \"your-token\"})"
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="isDefault"
              control={control}
              render={({ field }) => (
                <AppSwitch {...field} label="Is Default?" />
              )}
            />
          </Grid>

          {smsIntegrationDetail && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSmsIntegrationSubmit)}
          disabled={isBusy}
        >
          {isBusy ? "Saving..." : smsIntegrationDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default SmsIntegrationForm;